/**
 * Player Engagement PDF — Letter portrait, light print.
 * Miller QA: age-stacked G1-G4, G5 donut, header align, ASCII-only text.
 * PE_CHART_POLISH_IMPL_CLEAR
 */

export type PePdfBandVals = {
  uniques: number;
  sessions: number;
  created: number;
  avg: number;
};

export type PePdfMonthPoint = {
  month: string;
  uniques: number;
  sessions: number;
  created: number;
  avg: number;
  byBand: Record<string, PePdfBandVals>;
};

export type PePdfMixSlice = { name: string; value: number; color: string };

export type PePdfKpis = {
  uniqueActives: number;
  accountsCreated: number;
  sessions: number;
  avgLogins: number;
};

export type PePdfInput = {
  casinoLabel: string;
  fromMonth: string;
  toMonth: string;
  ageLabel: string;
  genderLabel: string;
  generatedAt: Date;
  kpis: PePdfKpis;
  ageBands: string[];
  bandColors: string[]; // hex, parallel to ageBands
  monthSeries: PePdfMonthPoint[];
  g5Month: string;
  /** G5 views: Age / Gender / Gender counts by Age Range */
  g5Views: Array<{ label: string; slices: PePdfMixSlice[] }>;
};

const PAGE_W = 612;
const PAGE_H = 792;
const MARGIN = 36;
const CONTENT_W = PAGE_W - MARGIN * 2;

const COL = {
  ink: [0.12, 0.14, 0.18] as RGB,
  muted: [0.4, 0.43, 0.48] as RGB,
  soft: [0.55, 0.58, 0.62] as RGB,
  line: [0.78, 0.8, 0.84] as RGB,
  zebra: [0.97, 0.98, 0.99] as RGB,
  headerBg: [0.12, 0.14, 0.18] as RGB,
  gold: [0.85, 0.65, 0.13] as RGB,
  white: [1, 1, 1] as RGB,
  page: [0.96, 0.97, 0.98] as RGB,
};

type RGB = [number, number, number];

const hexToRgb = (hex: string): RGB => {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
};

/** ASCII-only for Helvetica Type1 (no ? corruption). */
const escapePdf = (s: string) =>
  String(s ?? '')
    .normalize('NFKD')
    .replace(/[\u2013\u2014\u2212]/g, '-')
    .replace(/[\u2192\u2190\u00B7\u2022]/g, '-')
    .replace(/[^\x20-\x7E]/g, '')
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)');

const fmtInt = (n: number) => Math.round(Number(n) || 0).toLocaleString('en-US');
const fmtAvg = (n: number) =>
  (Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 1 });

function niceMax(n: number): number {
  if (!Number.isFinite(n) || n <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(n)));
  const m = n / pow;
  const nice = m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10;
  return nice * pow;
}

/** Locked SoT: yMax = niceMax(peak * 1.10). */
function peYMax(peak: number): number {
  if (!Number.isFinite(peak) || peak <= 0) return 1;
  return niceMax(peak * 1.1);
}

const rgbOp = (c: RGB, op: 'rg' | 'RG' = 'rg') => `${c[0].toFixed(3)} ${c[1].toFixed(3)} ${c[2].toFixed(3)} ${op}`;

class PePdfDoc {
  private pages: string[] = [];
  private content: string[] = [];
  private y = PAGE_H - MARGIN;
  private pageNo = 0;
  private filename = '';
  private casino = '';
  private range = '';

  setMeta(filename: string, casino: string, range: string) {
    this.filename = filename;
    this.casino = casino;
    this.range = range;
  }

  private flush() {
    if (!this.content.length) return;
    this.footer();
    this.pages.push(this.content.join('\n'));
    this.content = [];
  }

  newPage(continued = false) {
    this.flush();
    this.pageNo += 1;
    this.content.push(rgbOp(COL.page), `0 0 ${PAGE_W} ${PAGE_H} re`, 'f');
    if (continued) {
      const h = 28;
      const top = PAGE_H - 16;
      this.content.push(rgbOp(COL.headerBg), `0 ${top - h} ${PAGE_W} ${h + 16} re`, 'f');
      this.text(MARGIN, top - 18, 'Player Engagement - continued', 11, true, COL.white);
      this.text(PAGE_W - MARGIN - 210, top - 18, `${this.casino} - ${this.range}`, 8, false, [
        0.75, 0.78, 0.82,
      ]);
      this.y = top - h - 14;
    } else {
      this.y = PAGE_H - MARGIN;
    }
  }

  /** Dark header: left brand+title, right meta block vertically aligned. */
  mainHeader(casino: string, range: string, generated: string) {
    const h = 56;
    const top = PAGE_H;
    this.content.push(rgbOp(COL.headerBg), `0 ${top - h} ${PAGE_W} ${h} re`, 'f');
    // left brand
    this.content.push(rgbOp(COL.gold), `${MARGIN} ${top - 28} 7 7 re`, 'f');
    this.text(MARGIN + 12, top - 27, 'SlotCheck', 9, true, COL.white);
    this.text(MARGIN, top - 46, 'Player Engagement', 18, true, COL.white);
    // right meta - top-aligned with SlotCheck row, stacked
    const rx = PAGE_W - MARGIN - 200;
    this.text(rx, top - 22, casino, 9, true, COL.white);
    this.text(rx, top - 34, range, 8, false, [0.8, 0.82, 0.86]);
    this.text(rx, top - 46, generated, 8, false, [0.8, 0.82, 0.86]);
    this.y = top - h - 12;
  }

  private footer() {
    const total = 2;
    this.content.push(
      rgbOp(COL.line, 'RG'),
      '0.5 w',
      `${MARGIN} 32 m`,
      `${PAGE_W - MARGIN} 32 l`,
      'S'
    );
    this.text(MARGIN, 20, 'Source: SlotCheck Player Engagement - Cognito-gated report', 7, false, COL.soft);
    const right =
      this.pageNo === 1
        ? `Page 1 of ${total} - ${this.filename}`
        : `Page ${this.pageNo} of ${total}`;
    this.text(PAGE_W - MARGIN - 220, 20, right, 7, false, COL.soft);
  }

  text(x: number, y: number, str: string, size: number, bold = false, color: RGB = COL.ink) {
    this.content.push(
      rgbOp(color),
      'BT',
      `${bold ? '/F2' : '/F1'} ${size} Tf`,
      `1 0 0 1 ${x.toFixed(2)} ${y.toFixed(2)} Tm`,
      `(${escapePdf(str)}) Tj`,
      'ET'
    );
  }

  ensure(space: number) {
    if (this.y - space < 48) this.newPage(true);
  }

  section(title: string, helper: string) {
    this.ensure(34);
    this.text(MARGIN, this.y, title, 12, true);
    this.y -= 13;
    if (helper) {
      this.text(MARGIN, this.y, helper, 8, false, COL.muted);
      this.y -= 11;
    }
    this.y -= 2;
  }

  scopeRow(items: Array<{ label: string; value: string }>) {
    this.ensure(44);
    const h = 40;
    const top = this.y;
    this.content.push(
      rgbOp(COL.white),
      `${MARGIN} ${top - h} ${CONTENT_W} ${h} re`,
      'f',
      rgbOp(COL.line, 'RG'),
      '0.5 w',
      `${MARGIN} ${top - h} ${CONTENT_W} ${h} re`,
      'S'
    );
    const colW = CONTENT_W / items.length;
    items.forEach((it, i) => {
      const x = MARGIN + i * colW + 8;
      this.text(x, top - 14, it.label.toUpperCase(), 7, true, COL.muted);
      this.text(x, top - 30, it.value, 10, true);
    });
    this.y = top - h - 12;
  }

  kpiRow(items: Array<{ label: string; value: string; hint: string }>) {
    this.ensure(70);
    const h = 62;
    const gap = 8;
    const colW = (CONTENT_W - gap * (items.length - 1)) / items.length;
    const top = this.y;
    items.forEach((it, i) => {
      const x = MARGIN + i * (colW + gap);
      this.content.push(
        rgbOp(COL.white),
        `${x} ${top - h} ${colW} ${h} re`,
        'f',
        rgbOp(COL.line, 'RG'),
        '0.5 w',
        `${x} ${top - h} ${colW} ${h} re`,
        'S',
        rgbOp(COL.gold),
        `${x} ${top - 3} ${colW} 3 re`,
        'f'
      );
      this.text(x + 8, top - 16, it.label, 7, true, COL.muted);
      this.text(x + 8, top - 34, it.value, 15, true);
      this.text(x + 8, top - 50, it.hint, 6, false, COL.soft);
    });
    this.y = top - h - 12;
  }

  /** Compact 2×4 age-band legend (half-width cards). */
  private legend(x: number, y: number, bands: string[], colors: string[], maxW = CONTENT_W / 2 - 16) {
    const cols = 4;
    const colW = maxW / cols;
    bands.forEach((b, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const cx = x + col * colW;
      const cy = y - row * 9;
      const c = hexToRgb(colors[i] || '#888888');
      this.content.push(rgbOp(c), `${cx} ${cy - 1} 5 5 re`, 'f');
      this.text(cx + 7, cy, b, 5.5, false, COL.muted);
    });
  }

  private yTicks(plotX: number, plotY: number, plotW: number, plotH: number, max: number) {
    const ticks = 3;
    for (let i = 0; i <= ticks; i++) {
      const t = i / ticks;
      const yy = plotY + t * plotH;
      const val = Math.round(max * t);
      this.content.push(
        rgbOp(COL.line, 'RG'),
        '0.3 w',
        `${plotX} ${yy.toFixed(2)} m`,
        `${(plotX + plotW).toFixed(2)} ${yy.toFixed(2)} l`,
        'S'
      );
      this.text(plotX - 2, yy - 2, String(val), 6, false, COL.soft);
    }
  }

  private stackedBars(
    plotX: number,
    plotY: number,
    plotW: number,
    plotH: number,
    series: PePdfMonthPoint[],
    bands: string[],
    colors: string[],
    key: 'uniques' | 'sessions' | 'created'
  ) {
    const totals = series.map((m) =>
      bands.reduce((s, b) => s + (m.byBand[b]?.[key] || 0), 0)
    );
    const max = peYMax(Math.max(...totals, 0));
    const n = Math.max(series.length, 1);
    const gap = 1.5;
    const barW = Math.max(3, (plotW - 28 - gap * (n - 1)) / n);
    const baseX = plotX + 26;
    this.yTicks(baseX, plotY, plotW - 28, plotH, max);
    series.forEach((m, i) => {
      let y0 = plotY;
      const bx = baseX + i * (barW + gap);
      bands.forEach((b, bi) => {
        const v = m.byBand[b]?.[key] || 0;
        if (v <= 0) return;
        const bh = (v / max) * (plotH - 2);
        this.content.push(
          rgbOp(hexToRgb(colors[bi] || '#888')),
          `${bx.toFixed(2)} ${y0.toFixed(2)} ${barW.toFixed(2)} ${Math.max(0.4, bh).toFixed(2)} re`,
          'f'
        );
        y0 += bh;
      });
    });
    // sparse month labels
    const step = Math.max(1, Math.ceil(n / 4));
    series.forEach((m, i) => {
      if (i % step !== 0 && i !== n - 1) return;
      const bx = baseX + i * (barW + gap);
      this.text(bx, plotY - 8, m.month.slice(2), 5, false, COL.soft);
    });
  }

  private multiLines(
    plotX: number,
    plotY: number,
    plotW: number,
    plotH: number,
    series: PePdfMonthPoint[],
    bands: string[],
    colors: string[]
  ) {
    const all = series.flatMap((m) => bands.map((b) => m.byBand[b]?.avg || 0));
    const max = peYMax(Math.max(...all, 0));
    const n = series.length;
    const baseX = plotX + 26;
    const usableW = plotW - 28;
    this.yTicks(baseX, plotY, usableW, plotH, max);
    if (n < 2) return;
    bands.forEach((b, bi) => {
      const c = hexToRgb(colors[bi] || '#888');
      this.content.push(rgbOp(c, 'RG'), '1 w');
      series.forEach((m, i) => {
        const v = m.byBand[b]?.avg || 0;
        const px = baseX + (i / (n - 1)) * usableW;
        const py = plotY + (v / max) * (plotH - 2);
        this.content.push(i === 0 ? `${px.toFixed(2)} ${py.toFixed(2)} m` : `${px.toFixed(2)} ${py.toFixed(2)} l`);
      });
      this.content.push('S');
    });
  }

  /** Approximate donut with triangular wedges + white hole. */
  private donut(
    cx: number,
    cy: number,
    rOuter: number,
    rInner: number,
    slices: PePdfMixSlice[],
    centerLabel: string
  ) {
    const total = slices.reduce((s, d) => s + d.value, 0) || 1;
    let angle = -Math.PI / 2;
    const wedge = (a0: number, a1: number, color: RGB) => {
      const steps = Math.max(8, Math.ceil(((a1 - a0) * 24) / Math.PI));
      const pts: Array<[number, number]> = [[cx, cy]];
      for (let i = 0; i <= steps; i++) {
        const a = a0 + ((a1 - a0) * i) / steps;
        pts.push([cx + Math.cos(a) * rOuter, cy + Math.sin(a) * rOuter]);
      }
      this.content.push(rgbOp(color));
      pts.forEach(([x, y], i) => {
        this.content.push(i === 0 ? `${x.toFixed(2)} ${y.toFixed(2)} m` : `${x.toFixed(2)} ${y.toFixed(2)} l`);
      });
      this.content.push('f');
    };
    slices.forEach((sl) => {
      const span = (sl.value / total) * Math.PI * 2;
      wedge(angle, angle + span, hexToRgb(sl.color));
      angle += span;
    });
    // hole
    this.content.push(rgbOp(COL.white));
    const holeSteps = 36;
    for (let i = 0; i <= holeSteps; i++) {
      const a = (i / holeSteps) * Math.PI * 2;
      const x = cx + Math.cos(a) * rInner;
      const y = cy + Math.sin(a) * rInner;
      this.content.push(i === 0 ? `${x.toFixed(2)} ${y.toFixed(2)} m` : `${x.toFixed(2)} ${y.toFixed(2)} l`);
    }
    this.content.push('f');
    this.text(cx - 18, cy + 2, centerLabel, 9, true);
    this.text(cx - 16, cy - 10, 'linked', 7, false, COL.muted);
  }

  trendsGrid(series: PePdfMonthPoint[], bands: string[], colors: string[]) {
    this.ensure(230);
    const top = this.y;
    const gap = 8;
    const cardW = (CONTENT_W - gap) / 2;
    const cardH = 108;

    const card = (
      x: number,
      yTop: number,
      title: string,
      draw: (px: number, py: number, pw: number, ph: number) => void
    ) => {
      this.content.push(
        rgbOp(COL.white),
        `${x} ${yTop - cardH} ${cardW} ${cardH} re`,
        'f',
        rgbOp(COL.line, 'RG'),
        '0.5 w',
        `${x} ${yTop - cardH} ${cardW} ${cardH} re`,
        'S'
      );
      this.text(x + 6, yTop - 12, title, 8, true);
      this.legend(x + 6, yTop - 22, bands, colors, cardW - 12);
      // plot below 2-row legend; axis labels in bottom band
      draw(x + 6, yTop - cardH + 14, cardW - 12, cardH - 52);
    };

    card(MARGIN, top, 'G1 - Active Linked Accounts over time', (x, y, w, h) =>
      this.stackedBars(x, y, w, h, series, bands, colors, 'uniques')
    );
    card(MARGIN + cardW + gap, top, 'G2 - Sessions over time', (x, y, w, h) =>
      this.stackedBars(x, y, w, h, series, bands, colors, 'sessions')
    );
    card(MARGIN, top - cardH - gap, 'G3 - Avg logins per active player', (x, y, w, h) =>
      this.multiLines(x, y, w, h, series, bands, colors)
    );
    card(MARGIN + cardW + gap, top - cardH - gap, 'G4 - New Linked Accounts', (x, y, w, h) =>
      this.stackedBars(x, y, w, h, series, bands, colors, 'created')
    );
    this.y = top - cardH * 2 - gap - 12;
  }

  mixDonut(month: string, slices: PePdfMixSlice[], modeLabel: string) {
    // Larger donut ABOVE, tighter 2-col legend BELOW (count · pct% one line)
    const legendCols = slices.length > 12 ? 3 : 2;
    const rowsPerCol = Math.max(1, Math.ceil(Math.max(slices.length, 1) / legendCols));
    const donutBlock = 110;
    const legendH = rowsPerCol * 11 + 6;
    const h = 34 + donutBlock + legendH;
    this.ensure(h + 16);
    const top = this.y;
    this.content.push(
      rgbOp(COL.white),
      `${MARGIN} ${top - h} ${CONTENT_W} ${h} re`,
      'f',
      rgbOp(COL.line, 'RG'),
      '0.5 w',
      `${MARGIN} ${top - h} ${CONTENT_W} ${h} re`,
      'S'
    );
    this.text(MARGIN + 8, top - 14, `Linked Accounts for ${month} UTC (${modeLabel})`, 9, true);
    this.text(MARGIN + 8, top - 26, 'Selected-month composition.', 7, false, COL.muted);

    const total = slices.reduce((s, d) => s + d.value, 0);
    const cx = MARGIN + CONTENT_W / 2;
    const cy = top - 34 - donutBlock / 2;
    if (slices.length && total > 0) {
      this.donut(cx, cy, 44, 25, slices, fmtInt(total));
    } else {
      this.text(cx - 20, cy, 'No data', 9, false, COL.muted);
    }

    const legendTop = top - 34 - donutBlock - 2;
    const colW = CONTENT_W / legendCols;
    slices.forEach((d, i) => {
      const col = Math.floor(i / rowsPerCol);
      const row = i % rowsPerCol;
      const lx = MARGIN + 10 + col * colW;
      const ly = legendTop - row * 11;
      const pct = total ? ((d.value / total) * 100).toFixed(1) : '0.0';
      this.content.push(rgbOp(hexToRgb(d.color)), `${lx} ${ly - 1} 6 6 re`, 'f');
      this.text(lx + 10, ly, `${d.name} · ${fmtInt(d.value)} · ${pct}%`, 7.5);
    });
    this.y = top - h - 12;
  }

  table(headers: string[], rows: string[][], colWeights?: number[]) {
    const weights = colWeights ?? headers.map(() => 1);
    const sum = weights.reduce((a, b) => a + b, 0);
    const widths = weights.map((w) => (CONTENT_W * w) / sum);
    const rowH = 13;
    const headerH = 15;
    this.ensure(headerH + rows.length * rowH + 16);
    const estH = headerH + rows.length * rowH + 8;
    const top = this.y;
    this.content.push(
      rgbOp(COL.white),
      `${MARGIN} ${top - estH} ${CONTENT_W} ${estH} re`,
      'f',
      rgbOp(COL.line, 'RG'),
      '0.5 w',
      `${MARGIN} ${top - estH} ${CONTENT_W} ${estH} re`,
      'S'
    );
    let y = top;
    const draw = (cells: string[], yy: number, header: boolean) => {
      if (header) {
        this.content.push(
          '0.92 0.93 0.95 rg',
          `${MARGIN} ${yy - headerH + 3} ${CONTENT_W} ${headerH} re`,
          'f'
        );
      }
      let x = MARGIN;
      cells.forEach((cell, i) => {
        this.text(x + 3, yy - 9, cell.length > 36 ? `${cell.slice(0, 34)}...` : cell, 8, header);
        x += widths[i];
      });
    };
    draw(headers, y, true);
    y -= headerH;
    rows.forEach((row, idx) => {
      if (idx % 2 === 1) {
        this.content.push(rgbOp(COL.zebra), `${MARGIN} ${y - rowH + 3} ${CONTENT_W} ${rowH} re`, 'f');
      }
      draw(row, y, false);
      y -= rowH;
    });
    this.y = y - 10;
  }

  reservedBox() {
    this.ensure(50);
    const h = 44;
    const top = this.y;
    this.content.push(
      '0.94 0.95 0.97 rg',
      `${MARGIN} ${top - h} ${CONTENT_W} ${h} re`,
      'f',
      rgbOp(COL.line, 'RG'),
      '0.5 w',
      `${MARGIN} ${top - h} ${CONTENT_W} ${h} re`,
      'S'
    );
    this.text(MARGIN + 10, top - 16, 'Optional (not v1) - reserved', 9, true, COL.muted);
    this.text(
      MARGIN + 10,
      top - 30,
      'What-moved narrative / board one-pager stay out of v1 PDF.',
      8,
      false,
      COL.soft
    );
    this.y = top - h - 10;
  }

  finish(): Blob {
    this.flush();
    const pageCount = this.pages.length;
    type Obj = { id: number; body: string };
    const objs: Obj[] = [];
    const catalogId = 1;
    const pagesId = 2;
    const fontRegular = 3;
    const fontBold = 4;
    let nextId = 5;
    objs.push({ id: fontRegular, body: '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>' });
    objs.push({ id: fontBold, body: '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>' });
    const contentIds: number[] = [];
    for (let i = 0; i < pageCount; i++) {
      const id = nextId++;
      contentIds.push(id);
      const stream = this.pages[i];
      objs.push({ id, body: `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream` });
    }
    const pageIds: number[] = [];
    for (let i = 0; i < pageCount; i++) {
      const id = nextId++;
      pageIds.push(id);
      objs.push({
        id,
        body: `<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 ${PAGE_W} ${PAGE_H}] /Contents ${contentIds[i]} 0 R /Resources << /Font << /F1 ${fontRegular} 0 R /F2 ${fontBold} 0 R >> >> >>`,
      });
    }
    objs.push({
      id: pagesId,
      body: `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pageCount} >>`,
    });
    objs.push({ id: catalogId, body: `<< /Type /Catalog /Pages ${pagesId} 0 R >>` });
    objs.sort((a, b) => a.id - b.id);
    const maxId = objs[objs.length - 1].id;
    let pdf = '%PDF-1.4\n';
    const offsets: number[] = [0];
    for (let i = 1; i <= maxId; i++) {
      const obj = objs.find((o) => o.id === i)!;
      offsets[i] = pdf.length;
      pdf += `${i} 0 obj\n${obj.body}\nendobj\n`;
    }
    const xrefPos = pdf.length;
    pdf += `xref\n0 ${maxId + 1}\n0000000000 65535 f \n`;
    for (let i = 1; i <= maxId; i++) {
      pdf += `${String(offsets[i]).padStart(10, '0')} 00000 n \n`;
    }
    pdf += `trailer\n<< /Size ${maxId + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefPos}\n%%EOF`;
    return new Blob([pdf], { type: 'application/pdf' });
  }
}

export function buildPePdfFilename(casino: string, from: string, to: string): string {
  const safe = (s: string) =>
    (s || 'Casino')
      .replace(/[^\w\-]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, 48) || 'Casino';
  return `PE_${safe(casino)}_${from}_${to}.pdf`;
}

export function buildPlayerEngagementPdf(input: PePdfInput): Blob {
  const doc = new PePdfDoc();
  const filename = buildPePdfFilename(input.casinoLabel, input.fromMonth, input.toMonth);
  const range = `${input.fromMonth} to ${input.toMonth} UTC`;
  const when = `Generated ${input.generatedAt.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
  doc.setMeta(filename, input.casinoLabel, range);

  doc.newPage(false);
  doc.mainHeader(input.casinoLabel, range, when);

  doc.section('Report scope', 'Filters applied to every chart and total below.');
  doc.scopeRow([
    { label: 'Casino', value: input.casinoLabel },
    { label: 'Months (UTC)', value: `${input.fromMonth} - ${input.toMonth}` },
    { label: 'Age bands', value: input.ageLabel },
    { label: 'Gender', value: input.genderLabel },
  ]);

  doc.section('At a glance', 'Key totals for this filtered range (not a single month).');
  doc.kpiRow([
    {
      label: 'Active Linked Accounts',
      value: fmtInt(input.kpis.uniqueActives),
      hint: 'Distinct linked players active in range.',
    },
    {
      label: 'New Linked Accounts',
      value: fmtInt(input.kpis.accountsCreated),
      hint: 'New linked-account signups in range.',
    },
    {
      label: 'Sessions',
      value: fmtInt(input.kpis.sessions),
      hint: 'Total sessions attributed in range.',
    },
    {
      label: 'Avg logins / active',
      value: fmtAvg(input.kpis.avgLogins),
      hint: 'Sessions / Active Linked Accounts.',
    },
  ]);

  doc.section(
    'Trends',
    'Same filtered DataSet as the live report - age-stacked series with color legends.'
  );
  doc.trendsGrid(input.monthSeries, input.ageBands, input.bandColors);
  const g5Views = input.g5Views?.length
    ? input.g5Views
    : [];
  g5Views.forEach((view) => {
    doc.mixDonut(input.g5Month, view.slices, view.label);
  });

  doc.newPage(true);
  doc.section(
    'Month summary (top of range)',
    'Compact rollup - not the full Month x Age x Gender grid (export CSV for that)'
  );
  const rollup = [...input.monthSeries].reverse();
  doc.table(
    ['Month', 'Active Linked Accounts', 'Sessions', 'Avg logins', 'New Linked Accounts'],
    rollup.map((m) => [
      m.month,
      fmtInt(m.uniques),
      fmtInt(m.sessions),
      fmtAvg(m.avg),
      fmtInt(m.created),
    ]),
    [1.1, 1.2, 1.1, 1.1, 1.3]
  );

  doc.section('Metric definitions', 'How to read this snapshot');
  doc.table(
    ['Term', 'Definition'],
    [
      [
        'Active Linked Accounts',
        'Distinct linked player accounts with activity in the filtered month range.',
      ],
      [
        'New Linked Accounts',
        'New linked-account signups whose create month falls in the filtered range.',
      ],
      ['Sessions', 'Count of sessions attributed to linked accounts in range.'],
      ['Avg logins / active', 'Sessions / Active Linked Accounts for the same filters.'],
      ['Age bands', 'UTC age bands matching the live report filters.'],
      ['Months', 'UTC calendar month labels (report contract).'],
      ['Linked Accounts (G5)', 'Age / Gender / Gender-by-age composition for the latest month in range.'],
    ],
    [1.3, 2.7]
  );
  doc.reservedBox();

  return doc.finish();
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
